import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtLocalDateTime } from '../utils/datetime';
import { compareValues, type SortDir } from './SortableHeader';

// UI-2: show ISO datetimes via the shared local helper (MM/DD/YYYY HH:mm) so the
// filter chip matches the grid cell exactly.  The value stays raw for matching.
const ISO_DT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const fmtOpt = (v: string) => (ISO_DT_RE.test(v) ? (fmtLocalDateTime(v) || v) : v);

/**
 * Excel-style multi-select column filter dropdown.
 *
 * Behaviour (matches DevExpress / Excel header-filter):
 * ─────────────────────────────────────────────────────
 *  • If `selected === null` ⇒ filter is INACTIVE → every row passes.
 *  • Otherwise only rows whose stringified value is IN `selected` pass.
 *  • `''` (empty string) inside `selected` represents the (Blanks) row.
 *  • UI helpers (do not store extra state):
 *      – (All)        master checkbox – check all / uncheck all
 *      – (Blanks)     toggles `''` in/out of the selection
 *      – (Non blanks) toggles every non-empty distinct value in/out
 *      – Search box   filters the *option list* (does not affect the filter)
 *
 * The component is fully controlled — its parent owns the `selected` state
 * via `value` + `onChange`.
 */
export type ColumnFilterValue = Set<string> | null;

interface Props {
  /** Distinct values present in the column (no need to pre-sort or de-dup). */
  options: string[];
  /** Current selection. `null` ⇒ inactive (all pass). */
  value: ColumnFilterValue;
  /** Called whenever the selection changes. */
  onChange: (v: ColumnFilterValue) => void;
  /** Anchor element below which the popup is positioned. */
  anchor: HTMLElement | null;
  /** Close requested by user (outside click, Escape, or × button). */
  onClose: () => void;
  /** Column label – shown in the popup header for context. */
  columnLabel?: string;
}

const BLANK_LABEL    = '(Blanks)';
const NONBLANK_LABEL = '(Non blanks)';

/**
 * The ONE normalisation of a filter key. Both sides of the filter MUST go through
 * this: the option list that becomes the selected Set, and the row lookup in
 * passesColumnFilter. It is a shared function rather than a duplicated `.trim()`
 * precisely so the two cannot drift apart.
 *
 * They did drift, and the failure was silent. The option was built from the raw cell
 * while the lookup trimmed, so a value carrying a trailing space produced a Set key
 * the lookup could never ask for: the grid filtered to ZERO rows, with the option
 * sitting ticked in the list looking perfectly normal (HTML collapses the space).
 * Reported 2026-08-26 — SDMS Result Validation → Test → "Colilert-18 Quantitray"
 * returned nothing while every other test worked, because dbo.TestMethod stores
 * `Colilert-18 Quantitray ` and it is the ONLY padded value in the database.
 *
 * Note how it hid on the SQL side too: `WHERE TestName <> LTRIM(RTRIM(TestName))`
 * finds NOTHING, because SQL Server's string comparison ignores trailing spaces. Only
 * a DATALENGTH comparison surfaces it.
 *
 * Trimming here also merges values differing only by whitespace into one option —
 * correct for a filter, and it then matches every row behind it.
 */
export function filterKey(v: unknown): string {
  return (v ?? '').toString().trim();
}

export function ColumnFilterDropdown({
  options, value, onChange, anchor, onClose, columnLabel,
}: Props) {
  const [query, setQuery] = useState('');
  const popRef = useRef<HTMLDivElement>(null);

  // Distinct options, PRESERVING the incoming order.  The caller (ColFilterButton)
  // already orders `options` to match the grid column's current sort direction
  // (desc column → desc list), so the dropdown must NOT re-sort here — doing so
  // (the old ascending localeCompare) is exactly the bug where the filter list
  // diverged from the column sort.  Empty strings surface as a single "(Blanks)"
  // row.  A JS Set preserves insertion order, so de-dup keeps the caller's order.
  /* TRIMMED, and it must stay trimmed: this Set becomes the filter VALUE (the
     checkbox key, and `allSet` below), while passesColumnFilter() looks a row up as
     `value.has(cell.toString().trim())`. Build the option untrimmed and the two sides
     silently disagree — the option shows in the list (HTML collapses the space, so it
     looks identical), the user ticks it, and the lookup for the trimmed cell misses
     the padded key: the grid filters to ZERO rows with no error.
     Reported 2026-08-26 on SDMS Result Validation → Test → "Colilert-18 Quantitray",
     which filtered to nothing while every other test worked. The cause was one space
     in the data: dbo.TestMethod holds `Colilert-18 Quantitray ` and it is the ONLY
     padded value in the database (checked with DATALENGTH, not `<>` — SQL Server's
     comparison IGNORES trailing spaces, so `TestName <> LTRIM(RTRIM(TestName))`
     reports zero and hides exactly this class of bug).
     Fixed here rather than in the data because the mismatch is ours: any padded value
     in ANY column of ANY of the 8 grids using passesColumnFilter would filter to
     nothing. Trimming also merges values that differ only by whitespace into one
     option — correct for a filter, and it then matches every one of those rows. */
  const { distinctValues, hasBlanks } = useMemo(() => {
    const set = new Set<string>();
    let blank = false;
    for (const o of options) {
      const v = filterKey(o);
      if (v === '') blank = true;
      else          set.add(v);
    }
    return { distinctValues: Array.from(set), hasBlanks: blank };
  }, [options]);

  // Filtered list shown in the option pane (search box narrows display only).
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return distinctValues;
    return distinctValues.filter(v => v.toLowerCase().includes(q));
  }, [distinctValues, query]);

  // Compute "everything would pass" set – used when toggling (All) on.
  const allSet = useMemo(() => {
    const s = new Set(distinctValues);
    if (hasBlanks) s.add('');
    return s;
  }, [distinctValues, hasBlanks]);

  // What's currently treated as "selected" for rendering checkboxes:
  // when value === null we visually show (All) checked.
  const effective = value ?? allSet;

  // ── Position the popup, CLAMPED to the viewport ──────────────────────────
  // Fixes the last/right-most column's filter opening off-screen: it flips to
  // right-align near the right edge, opens upward when there's no room below,
  // and caps its height to the available space so the list always scrolls into
  // view.  Applied here so every grid in the app benefits.
  const [pos, setPos] = useState<{ top: number; left: number; minW: number; maxH: number }>({
    top: 0, left: 0, minW: 220, maxH: 360,
  });
  useEffect(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const POP_W = 240;
    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: left-align to the anchor, but if that overflows the right
    // edge, right-align the popup to the anchor instead (open leftwards) — then
    // hard-clamp so it can NEVER exceed either viewport edge (the anchor itself
    // may sit past the edge when the grid is scrolled horizontally).
    let left = r.left;
    if (left + POP_W > vw - MARGIN) left = r.right - POP_W;
    left = Math.min(left, vw - POP_W - MARGIN);
    left = Math.max(MARGIN, left);

    // Vertical: prefer below; flip above when there's more room up top.
    const spaceBelow = vh - r.bottom - MARGIN;
    const spaceAbove = r.top - MARGIN;
    const openAbove = spaceBelow < 200 && spaceAbove > spaceBelow;
    const maxH = Math.max(140, Math.min(360, openAbove ? spaceAbove : spaceBelow));
    const top = openAbove ? r.top - maxH - 2 : r.bottom + 2;

    setPos({
      top: top + window.scrollY,
      left: left + window.scrollX,
      minW: Math.max(220, r.width),
      maxH,
    });
  }, [anchor]);

  // ── Close on outside click / Escape ──────────────────────────────────────
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)
          && anchor && !anchor.contains(e.target as Node)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown',   onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown',   onKey);
    };
  }, [anchor, onClose]);

  // ── Toggle helpers ───────────────────────────────────────────────────────
  function toggleValue(v: string) {
    const cur = new Set(effective);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    // If the resulting set covers everything available → treat as inactive
    if (cur.size === allSet.size) onChange(null);
    else                          onChange(cur);
  }
  function setAll(checked: boolean) {
    onChange(checked ? null : new Set<string>());
  }
  function setBlanks(checked: boolean) {
    const cur = new Set(effective);
    if (checked) cur.add(''); else cur.delete('');
    if (cur.size === allSet.size) onChange(null); else onChange(cur);
  }
  function setNonBlanks(checked: boolean) {
    const cur = new Set(effective);
    if (checked) {
      distinctValues.forEach(v => cur.add(v));
    } else {
      distinctValues.forEach(v => cur.delete(v));
    }
    if (cur.size === allSet.size) onChange(null); else onChange(cur);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const allChecked      = value === null;
  const blanksChecked   = hasBlanks && effective.has('');
  const nonBlanksChecked = distinctValues.length > 0
    && distinctValues.every(v => effective.has(v));

  return createPortal(
    <div
      ref={popRef}
      className="col-fdd"
      style={{ top: pos.top, left: pos.left, minWidth: pos.minW, maxHeight: pos.maxH }}
      role="dialog"
      aria-label={`Filter ${columnLabel ?? ''}`}
    >
      <div className="col-fdd-hdr">
        <span>Filter {columnLabel ? `· ${columnLabel}` : ''}</span>
        <button className="col-fdd-close" onClick={onClose} title="Close">×</button>
      </div>

      <div className="col-fdd-search">
        <i className="ti ti-search" />
        <input
          autoFocus
          type="text"
          placeholder="Enter text to filter…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query && (
          <span className="col-fdd-clear" onClick={() => setQuery('')}>×</span>
        )}
      </div>

      <div className="col-fdd-list">
        {/* (Select All) — master toggle, ALWAYS visible (incl. while searching) so
            the user can clear EVERY value in one click and then tick only the
            one(s) they want.  It operates on the WHOLE value set (not just the
            search-filtered rows), so "unselect all" truly deselects everything and
            the searched value is the only thing left to tick.  (Previously hidden
            once a search query was typed — which blocked exactly this flow.) */}
        {(distinctValues.length > 0 || hasBlanks) && (
          <label className="col-fdd-row col-fdd-row-meta">
            <input
              type="checkbox"
              checked={allChecked}
              ref={el => { if (el) el.indeterminate = !allChecked && effective.size > 0; }}
              // Derive the action from React state, NOT e.target.checked: a click
              // on an *indeterminate* checkbox reports an unreliable `checked`
              // across browsers, which could make "uncheck all" misfire. allChecked
              // ⇒ uncheck all (empty Set → 0 rows); otherwise ⇒ check all (null).
              onChange={() => setAll(!allChecked)}
            />
            <span>(Select All)</span>
          </label>
        )}

        {/* (Blanks) – when column has any empty cells and no search */}
        {!query && hasBlanks && (
          <label className="col-fdd-row col-fdd-row-meta">
            <input
              type="checkbox"
              checked={blanksChecked}
              onChange={e => setBlanks(e.target.checked)}
            />
            <span>{BLANK_LABEL}</span>
          </label>
        )}

        {/* (Non blanks) – only when there are non-blank values and no search */}
        {!query && distinctValues.length > 0 && (
          <label className="col-fdd-row col-fdd-row-meta">
            <input
              type="checkbox"
              checked={nonBlanksChecked}
              ref={el => { if (el) el.indeterminate = !nonBlanksChecked
                && distinctValues.some(v => effective.has(v)); }}
              onChange={e => setNonBlanks(e.target.checked)}
            />
            <span>{NONBLANK_LABEL}</span>
          </label>
        )}

        {(hasBlanks || distinctValues.length > 0) && (
          <div className="col-fdd-sep" />
        )}

        {filteredOptions.length === 0 ? (
          <div className="col-fdd-empty">No matches</div>
        ) : filteredOptions.map(v => (
          <label key={v} className="col-fdd-row" title={fmtOpt(v)}>
            <input
              type="checkbox"
              checked={effective.has(v)}
              onChange={() => toggleValue(v)}
            />
            <span>{fmtOpt(v)}</span>
          </label>
        ))}
      </div>

      <div className="col-fdd-foot">
        <button className="col-fdd-clearall" onClick={() => onChange(null)}>
          <i className="ti ti-filter-off" /> Clear filter
        </button>
        <button className="col-fdd-ok" onClick={onClose}>OK</button>
      </div>

      {/* Styles — global, scoped via .col-fdd prefix */}
      <style>{`
        .col-fdd {
          position: absolute;
          background: #fff;
          border: 1px solid var(--ai-line);
          border-radius: 4px;
          box-shadow: 0 6px 22px rgba(0,0,0,.18);
          /* Above full-screen modal dialogs that use very high custom z-index
             (e.g. Custom Report Builder's 9000-12000) so the filter popup is never
             buried behind the modal it's anchored inside. */
          z-index: 13000;
          display: flex; flex-direction: column;
          max-height: 360px; width: 240px;
          font-family: inherit; font-size: 12px; color: var(--ai-ink);
        }
        .col-fdd-hdr {
          display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px; background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line);
          font-weight:700; color:var(--ai-ink);
        }
        .col-fdd-close {
          background:transparent; border:none; cursor:pointer;
          font-size:18px; line-height:1; color:var(--ai-muted); padding:0 4px;
        }
        .col-fdd-close:hover { color:var(--ai-ink); }
        .col-fdd-search {
          display:flex; align-items:center; gap:5px;
          padding:6px 8px; border-bottom:1px solid var(--ai-line-soft);
          background:var(--ai-header-bg);
        }
        .col-fdd-search i { font-size:13px; color:var(--ai-muted); }
        .col-fdd-search input {
          flex:1; border:1px solid var(--ai-line); border-radius:3px;
          padding:3px 6px; font-size:12px; outline:none; min-width:0;
          background:#fff;
        }
        .col-fdd-search input:focus { border-color:var(--brand-accent, var(--ai-orange)); }
        .col-fdd-clear {
          color:var(--ai-muted); cursor:pointer; padding:0 4px; font-size:14px; line-height:1;
        }
        .col-fdd-clear:hover { color:var(--ai-ink); }
        .col-fdd-list {
          flex:1; overflow-y:auto; padding:2px 0;
        }
        .col-fdd-list::-webkit-scrollbar { width:9px; }
        .col-fdd-list::-webkit-scrollbar-thumb { background:#c5cad4; border-radius:4px; }
        .col-fdd-list::-webkit-scrollbar-thumb:hover { background:#9aa0ae; }
        .col-fdd-list::-webkit-scrollbar-track { background:#f0f0f0; }
        .col-fdd-row {
          display:flex; align-items:center; gap:6px;
          padding:3px 10px; cursor:pointer;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .col-fdd-row:hover { background:#eef4fd; }
        .col-fdd-row input[type=checkbox] {
          width:13px; height:13px; flex-shrink:0; cursor:pointer; accent-color: var(--brand-accent, var(--ai-orange));
        }
        .col-fdd-row span {
          flex:1; overflow:hidden; text-overflow:ellipsis;
        }
        .col-fdd-row-meta { font-weight:600; color:var(--brand-accent, var(--ai-orange)); }
        .col-fdd-row-meta:hover { background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); }
        .col-fdd-sep {
          height:1px; background:var(--ai-line-soft); margin:4px 8px;
        }
        .col-fdd-empty {
          padding:10px; text-align:center; color:var(--ai-muted); font-style:italic;
        }
        .col-fdd-foot {
          display:flex; gap:6px; padding:6px 8px;
          border-top:1px solid var(--ai-line-soft); background:var(--ai-header-bg);
        }
        .col-fdd-clearall {
          flex:1;
          display:inline-flex; align-items:center; justify-content:center; gap:4px;
          background:#fff; border:1px solid var(--ai-line); border-radius:3px;
          padding:4px 8px; font-size:11px; color:var(--ai-red); cursor:pointer;
        }
        .col-fdd-clearall:hover { background:#fef2f2; border-color:#fca5a5; }
        .col-fdd-ok {
          background:var(--brand-accent, var(--ai-orange)); color:#fff; border:none; border-radius:3px;
          padding:4px 14px; font-size:12px; font-weight:600; cursor:pointer;
        }
        .col-fdd-ok:hover { background:var(--brand-accent-hover, var(--ai-orange-hover)); }

        /* Trigger-button styles (.col-flt-btn, .col-flt-chevron) and the
         * header-cell layout (.col-hdr-cell) live in backlog.css so they
         * are loaded even when this popup is not mounted. */
      `}</style>
    </div>,
    document.body,
  );
}

/**
 * Applies a per-column filter to a row.
 * @param value  filter selection (null = pass-through)
 * @param cell   string representation of the cell value
 */
export function passesColumnFilter(value: ColumnFilterValue, cell: string): boolean {
  if (value === null) return true;
  return value.has(filterKey(cell));
}

/**
 * Rows a single column's filter dropdown should offer (Excel-style cascade):
 * the input rows narrowed by every OTHER active column filter, so picking one
 * column limits the value lists of the rest. The edited column (`excludeKey`)
 * is skipped so it never restricts its own options. `text(row, key)` returns
 * the displayed string for a column — pass the SAME formatter the cell uses.
 */
export function cascadeRows<T>(
  rows: readonly T[],
  filters: Record<string, ColumnFilterValue>,
  excludeKey: string,
  text: (row: T, key: string) => string,
): T[] {
  const active = Object.entries(filters).filter(([k, sel]) => k !== excludeKey && sel != null);
  if (active.length === 0) return rows as T[];
  return rows.filter(r => active.every(([k, sel]) => passesColumnFilter(sel, text(r, k))));
}

/**
 * Header-row filter trigger. Renders a small button that opens the
 * multi-select dropdown for one column. Owns its own open/close state.
 *
 * Variants:
 *   • `block`   (default) — full-width button shown in the col-filter-row
 *                            (compact text-input replacement).
 *   • `inline`            — tiny chevron icon meant to sit inside the
 *                            column header next to the label (GoLive style).
 */
interface ButtonProps<T> {
  /** Column key – used as a stable identifier and for aria. */
  col: string;
  /** Human label (shown in the popup header). */
  label?: string;
  /** All rows (used to compute distinct option list). */
  rows: T[];
  /** Maps a row → the *formatted* cell string for this column. */
  formatter: (r: T) => string;
  /** Controlled filter state for this column. */
  value: ColumnFilterValue;
  /** Update handler. */
  onChange: (v: ColumnFilterValue) => void;
  /** Visual style of the trigger button. */
  variant?: 'block' | 'inline';
  /** The column's CURRENT sort direction.  When set, the filter value list is
   *  ordered the SAME way (desc column → desc list; asc → asc).  `null`/omitted
   *  ⇒ ascending (the safe default for callers that don't track sort). */
  sortDir?: SortDir;
  /** Raw value used to ORDER the list — for date/number columns pass the
   *  underlying value so ordering is by real value (chronological / numeric),
   *  not the formatted string.  Defaults to the formatted display text. */
  sortKey?: (r: T) => unknown;
}

export function ColFilterButton<T>({
  col, label, rows, formatter, value, onChange, variant = 'block',
  sortDir = null, sortKey,
}: ButtonProps<T>) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Distinct display values, ORDERED to match what the grid shows for this
  // column — so the filter list never disagrees with the visible rows.
  //   • EXPLICIT click-sort (sortDir = 'asc' | 'desc'): reproduce it exactly
  //     with the grid's own comparator (compareValues — number → date → locale)
  //     over the raw sort key, because the rows handed to us are NOT re-sorted
  //     by the click (the grid sorts a separate copy for display).
  //   • NO explicit sort (sortDir null/undefined): the rows are already in the
  //     grid's displayed order — a server/default sort such as JobID DESC — so
  //     PRESERVE that first-appearance order instead of forcing ascending.
  //     (This is the fix for "grid shows DESC but the filter listed ASC": a
  //     default-/server-sorted column has no client useSort state, so sortDir
  //     is null, yet the data is already DESC.)  Map insertion order = first
  //     appearance = displayed order.
  const options = useMemo(() => {
    const seen = new Map<string, unknown>();
    for (const r of rows) {
      const t = formatter(r);
      if (!seen.has(t)) seen.set(t, sortKey ? sortKey(r) : t);
    }
    const entries = Array.from(seen.entries());
    if (sortDir !== 'asc' && sortDir !== 'desc') {
      return entries.map(([t]) => t);          // preserve the displayed order
    }
    const mult = sortDir === 'desc' ? -1 : 1;
    return entries
      .sort(([, ka], [, kb]) => mult * compareValues(ka, kb))
      .map(([t]) => t);
  }, [rows, formatter, sortDir, sortKey]);
  const active  = value !== null;
  const text    = !active
    ? 'Filter…'
    : value.size === 0
      ? '(none)'
      : `${value.size} selected`;

  if (variant === 'inline') {
    return (
      <>
        <button
          ref={btnRef}
          type="button"
          className={`col-flt-chevron${active ? ' active' : ''}${open ? ' open' : ''}`}
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={active ? `Edit filter on ${label ?? col}` : `Filter ${label ?? col}`}
          data-col={col}
        >
          <i className={active ? 'ti ti-filter-filled' : 'ti ti-chevron-down'} />
        </button>
        {open && (
          <ColumnFilterDropdown
            options={options}
            value={value}
            onChange={onChange}
            anchor={btnRef.current}
            onClose={() => setOpen(false)}
            columnLabel={label ?? col}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`col-flt-btn${active ? ' active' : ''}`}
        // stopPropagation: prevents the parent `<th onClick>` (click-to-sort)
        // from re-sorting when the user clicks the filter funnel.
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title={active ? `${text} – click to edit` : 'Click to filter this column'}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-col={col}
      >
        <i className={active ? 'ti ti-filter-filled' : 'ti ti-filter'} />
        <span className="lbl">{text}</span>
        <i className="ti ti-chevron-down" />
      </button>
      {open && (
        <ColumnFilterDropdown
          options={options}
          value={value}
          onChange={onChange}
          anchor={btnRef.current}
          onClose={() => setOpen(false)}
          columnLabel={label ?? col}
        />
      )}
    </>
  );
}
